import React from 'react';
import { useInView } from '../hooks/useInView';
import { MapPin, Mountain, Compass } from 'lucide-react';

const photos = [
  { src: '/ride3.jpg', alt: 'Embracing the mountains', span: 'col-span-2 row-span-2' },
  { src: '/ride1.jpg', alt: 'Riding gear in the hills', span: '' },
  { src: '/ride4.jpg', alt: 'Royal Enfield Classic 350', span: '' },
  { src: '/ride2.jpg', alt: 'Bike overlooking valleys', span: 'col-span-2' },
];

const stats = [
  { icon: MapPin, value: '15+', label: 'States Explored' },
  { icon: Mountain, value: '12K ft', label: 'Highest Pass' },
  { icon: Compass, value: '20K+ km', label: 'Miles Ridden' },
];

const PassionSection = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.3 });
  const [storyRef, storyInView] = useInView({ threshold: 0.2 });
  const [gridRef, gridInView] = useInView({ threshold: 0.1 });
  const [statsRef, statsInView] = useInView({ threshold: 0.3 });

  return (
    <section className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div
          ref={headerRef}
          className={`mb-16 transition-all duration-700 ${
            headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="font-mono text-[12px] text-gray-400 tracking-widest uppercase mb-4">
            Beyond Code
          </p>
          <h2 className="font-heading text-4xl md:text-[3.25rem] font-[200] tracking-tight leading-tight">
            Ride & Explore<span className="text-gray-200">.</span>
          </h2>
        </div>

        {/* Story */}
        <div
          ref={storyRef}
          className={`mb-14 max-w-2xl transition-all duration-700 ${
            storyInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-gray-400 text-[17px] leading-relaxed">
            When I'm not writing code, you'll find me on two wheels
            chasing mountain passes and discovering new horizons. Riding through
            India's diverse landscapes — from the snow-capped peaks of Himachal to
            the lush Western Ghats — taught me resilience, adaptability, and the
            value of community.
          </p>
          <p className="text-gray-400 text-[17px] leading-relaxed mt-4">
            That passion for the riding community inspired me to build{' '}
            <span className="text-[#0a0a0a] font-medium">Riderpal</span> — a
            platform connecting riders, mapping routes, and nurturing the ecosystem
            I fell in love with on the road.
          </p>
        </div>

        {/* Riding stats */}
        <div
          ref={statsRef}
          className={`flex flex-wrap gap-10 mb-14 transition-all duration-700 ${
            statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              className="flex items-center gap-3"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-10 h-10 rounded-xl bg-[#fafafa] border border-gray-100 flex items-center justify-center">
                <s.icon size={16} className="text-gray-500" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-heading text-xl font-semibold tracking-tight">{s.value}</p>
                <p className="font-mono text-[10px] text-gray-300 tracking-wide">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Photo Grid */}
        <div
          ref={gridRef}
          className={`grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 transition-all duration-700 ${
            gridInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {photos.map((photo, i) => (
            <div
              key={photo.src}
              className={`${photo.span} rounded-2xl md:rounded-3xl overflow-hidden group ${
                photo.span.includes('row-span') ? '' : photo.span.includes('col-span-2') ? 'aspect-[2/1]' : 'aspect-square'
              }`}
              style={{ transitionDelay: `${200 + i * 100}ms` }}
            >
              <img
                src={photo.src}
                alt={photo.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PassionSection;
